import React, { useState, useMemo } from 'react';
import {
  Users, Search, Filter, MoreHorizontal, Mail, Shield, UserPlus,
  ChevronDown, Check, X, Building, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuCheckboxItem
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { cn } from '../../lib/utils';

const mockPeople = [
  {
    id: 'user-1',
    name: 'Emily Zhang',
    email: 'emily@company.com',
    avatar: 'EZ',
    orgRole: 'admin',
    teams: [
      { name: 'IT Operations', role: 'manager' },
      { name: 'Security', role: 'agent' }
    ],
    status: 'active',
    lastActive: '2 hours ago'
  },
  {
    id: 'user-2',
    name: 'David Park',
    email: 'david@company.com',
    avatar: 'DP',
    orgRole: 'admin',
    teams: [
      { name: 'Identity & Access', role: 'manager' }
    ],
    status: 'active',
    lastActive: '1 hour ago'
  },
  {
    id: 'user-3',
    name: 'Sarah Chen',
    email: 'sarah@company.com',
    avatar: 'SC',
    orgRole: 'member',
    teams: [
      { name: 'IT Operations', role: 'manager' },
      { name: 'Endpoint Management', role: 'agent' }
    ],
    status: 'active',
    lastActive: '30 min ago'
  },
  {
    id: 'user-4',
    name: 'Mike Torres',
    email: 'mike@company.com',
    avatar: 'MT',
    orgRole: 'member',
    teams: [
      { name: 'IT Operations', role: 'manager' }
    ],
    status: 'active',
    lastActive: '5 min ago'
  },
  {
    id: 'user-5',
    name: 'Alex Kim',
    email: 'alex@company.com',
    avatar: 'AK',
    orgRole: 'member',
    teams: [
      { name: 'Security', role: 'manager' }
    ],
    status: 'active',
    lastActive: '1 day ago'
  },
  {
    id: 'user-6',
    name: 'Jordan Lee',
    email: 'jordan@company.com',
    avatar: 'JL',
    orgRole: 'member',
    teams: [
      { name: 'Identity & Access', role: 'manager' }
    ],
    status: 'active',
    lastActive: '3 hours ago'
  },
  {
    id: 'user-7',
    name: 'Taylor Brown',
    email: 'taylor@company.com',
    avatar: 'TB',
    orgRole: 'member',
    teams: [
      { name: 'Endpoint Management', role: 'manager' }
    ],
    status: 'invited',
    lastActive: null
  },
  {
    id: 'user-8',
    name: 'Casey Wilson',
    email: 'casey@company.com',
    avatar: 'CW',
    orgRole: 'member',
    teams: [
      { name: 'IT Operations', role: 'agent' }
    ],
    status: 'active',
    lastActive: '2 days ago'
  }
];

const allTeams = ['IT Operations', 'Security', 'Identity & Access', 'Endpoint Management'];

const roleColors = {
  admin: 'bg-amber-100 text-amber-800 border-amber-200',
  member: 'bg-slate-100 text-slate-700 border-slate-200',
  manager: 'bg-blue-100 text-blue-800 border-blue-200',
  agent: 'bg-slate-100 text-slate-600 border-slate-200'
};

export const People = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedOrgRole, setSelectedOrgRole] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', orgRole: 'member', team: '', teamRole: 'agent' });

  const filteredPeople = useMemo(() => {
    return mockPeople.filter(person => {
      const matchesSearch = person.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        person.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTeam = selectedTeams.length === 0 ||
        person.teams.some(t => selectedTeams.includes(t.name));
      const matchesRole = !selectedOrgRole || person.orgRole === selectedOrgRole;
      return matchesSearch && matchesTeam && matchesRole;
    });
  }, [searchQuery, selectedTeams, selectedOrgRole]);

  const stats = {
    total: mockPeople.length,
    admins: mockPeople.filter(p => p.orgRole === 'admin').length,
    active: mockPeople.filter(p => p.status === 'active').length,
    invited: mockPeople.filter(p => p.status === 'invited').length
  };

  const toggleTeamFilter = (team) => {
    setSelectedTeams(prev =>
      prev.includes(team) ? prev.filter(t => t !== team) : [...prev, team]
    );
  };

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">People</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage users, org roles, and team assignments
              </p>
            </div>
            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Person
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Person</DialogTitle>
                  <DialogDescription>
                    Send an invitation to join your organization
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email Address</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="colleague@company.com"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Organization Role</Label>
                    <Select value={inviteData.orgRole} onValueChange={(v) => setInviteData({ ...inviteData, orgRole: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Org Admin</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Org Admins have access to all teams and settings
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Initial Team (Optional)</Label>
                    <Select value={inviteData.team} onValueChange={(v) => setInviteData({ ...inviteData, team: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a team" />
                      </SelectTrigger>
                      <SelectContent>
                        {allTeams.map(team => (
                          <SelectItem key={team} value={team}>{team}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {inviteData.team && (
                    <div className="space-y-2">
                      <Label>Team Role</Label>
                      <Select value={inviteData.teamRole} onValueChange={(v) => setInviteData({ ...inviteData, teamRole: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="agent">Agent</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Managers can approve workflows. Agents can use workflows.
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
                  <Button onClick={() => setShowInviteDialog(false)}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Invite
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total People</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.admins}</p>
                  <p className="text-xs text-muted-foreground">Org Admins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.active}</p>
                  <p className="text-xs text-muted-foreground">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.invited}</p>
                  <p className="text-xs text-muted-foreground">Pending Invites</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search people..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Building className="w-4 h-4 mr-2" />
                Team
                {selectedTeams.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{selectedTeams.length}</Badge>
                )}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {allTeams.map(team => (
                <DropdownMenuCheckboxItem
                  key={team}
                  checked={selectedTeams.includes(team)}
                  onCheckedChange={() => toggleTeamFilter(team)}
                >
                  {team}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Shield className="w-4 h-4 mr-2" />
                Role
                {selectedOrgRole && <Badge variant="secondary" className="ml-2">1</Badge>}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuCheckboxItem
                checked={selectedOrgRole === 'admin'}
                onCheckedChange={() => setSelectedOrgRole(selectedOrgRole === 'admin' ? null : 'admin')}
              >
                Org Admin
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={selectedOrgRole === 'member'}
                onCheckedChange={() => setSelectedOrgRole(selectedOrgRole === 'member' ? null : 'member')}
              >
                Member
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {(selectedTeams.length > 0 || selectedOrgRole) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedTeams([]); setSelectedOrgRole(null); }}
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Person</th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Org Role</th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Teams</th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Last Active</th>
                  <th className="w-12 p-4"></th>
                </tr>
              </thead>
              <tbody>
                {filteredPeople.map((person) => (
                  <tr key={person.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                          {person.avatar}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{person.name}</p>
                          <p className="text-xs text-muted-foreground">{person.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className={cn("capitalize", roleColors[person.orgRole])}>
                        {person.orgRole === 'admin' ? 'Org Admin' : 'Member'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {person.teams.map((team, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {team.name}
                            <span className={cn("ml-1 px-1 rounded text-[10px]", roleColors[team.role])}>
                              {team.role}
                            </span>
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant={person.status === 'active' ? 'secondary' : 'outline'} className="capitalize">
                        {person.status}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {person.lastActive || 'Never'}
                      </span>
                    </td>
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>Edit Roles</DropdownMenuItem>
                          <DropdownMenuItem>Add to Team</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive">Remove from Org</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default People;
